/**
 * <ch-header> — sticky header with a hover/click mega menu and a mobile
 * full-screen panel. Mirrors the behaviour in the CELLHACK design handoff's
 * Site Header.dc.html: mega menu opens on mouseenter of its trigger or on
 * click, closes on mouseleave of the whole header, Escape, or outside click.
 */
class CellhackHeader extends HTMLElement {
  connectedCallback() {
    this.hoverOpenedAt = 0;
    this.megaTrigger = this.querySelector('[data-ch-mega-toggle]');
    this.megaCaret = this.querySelector('[data-ch-mega-caret]');
    this.mega = this.querySelector('[data-ch-mega]');
    this.menuToggle = this.querySelector('[data-ch-menu-toggle]');
    this.menuLabel = this.querySelector('[data-ch-menu-label]');
    this.mobilePanel = this.querySelector('[data-ch-mobile]');

    if (this.megaTrigger && this.mega) {
      // Touch screens emit a synthetic mouseenter right before the click, so
      // hover-opening must be limited to real hover pointers and a click that
      // follows a hover-open must not immediately close the menu again.
      const canHover = window.matchMedia('(hover: hover)').matches;
      this.megaTrigger.addEventListener('click', () => {
        if (Date.now() - this.hoverOpenedAt < 600) return;
        this.toggleMega();
      });
      if (canHover) {
        this.addEventListener('mouseenter', () => {
          this.hoverOpenedAt = Date.now();
          this.openMega();
        });
        this.addEventListener('mouseleave', () => this.closeMega());
      }
    }

    if (this.menuToggle && this.mobilePanel) {
      this.menuToggle.addEventListener('click', () => this.toggleMobile());
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      this.closeMega();
      this.closeMobile();
    });

    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.closeMega();
    });
  }

  openMega() {
    if (!this.mega || this.mega.hidden === false) return;
    this.mega.hidden = false;
    this.megaTrigger.setAttribute('aria-expanded', 'true');
    this.megaTrigger.classList.add('is-open');
    if (this.megaCaret) this.megaCaret.textContent = '▲';
  }

  closeMega() {
    if (!this.mega || this.mega.hidden === true) return;
    this.mega.hidden = true;
    this.megaTrigger.setAttribute('aria-expanded', 'false');
    this.megaTrigger.classList.remove('is-open');
    if (this.megaCaret) this.megaCaret.textContent = '▼';
  }

  toggleMega() {
    if (this.mega.hidden) this.openMega();
    else this.closeMega();
  }

  toggleMobile() {
    const nowOpen = this.mobilePanel.hidden;
    if (nowOpen) {
      const header = this.querySelector('.ch-header');
      this.mobilePanel.style.top = Math.max(0, Math.round(header.getBoundingClientRect().bottom)) + 'px';
    }
    this.mobilePanel.hidden = !nowOpen;
    this.menuToggle.setAttribute('aria-expanded', String(nowOpen));
    if (this.menuLabel) this.menuLabel.textContent = nowOpen ? 'Close' : 'Menu';
    document.body.classList.toggle('ch-mobile-panel-open', nowOpen);
  }

  closeMobile() {
    if (!this.mobilePanel || this.mobilePanel.hidden) return;
    this.mobilePanel.hidden = true;
    this.menuToggle.setAttribute('aria-expanded', 'false');
    if (this.menuLabel) this.menuLabel.textContent = 'Menu';
    document.body.classList.remove('ch-mobile-panel-open');
  }
}

if (!customElements.get('ch-header')) {
  customElements.define('ch-header', CellhackHeader);
}
